export type PciDssControlStatus = 'IMPLEMENTED' | 'PARTIAL' | 'NOT_IMPLEMENTED' | 'N_A';
export type PciDssDomain = 'Network' | 'Data Protection' | 'Vulnerability' | 'Access' | 'Monitoring' | 'Policy';

export interface PciDssControl {
  standard: string;
  controlCode: string;
  domain: PciDssDomain;
  title: { en: string; es: string; ru: string };
  description: { en: string; es: string; ru: string };
  severity: 'CRITICAL' | 'MAJOR' | 'MODERATE';
  evidence: { type: string; examples: { en: string[]; es: string[]; ru: string[] } }[];
  vendorImpact: { applies: boolean };
  governanceLinks: { policies: string[]; processes: string[]; roles: string[] };
  crossMapping: { ISO27001: string[] };
  recommendations: { CRITICAL?: { en: string; es: string; ru: string }; MAJOR?: { en: string; es: string; ru: string } };
}

const ev = (t: string, en: string[], es: string[], ru: string[]) => ({ type: t, examples: { en, es, ru } });
const def = (code: string, domain: PciDssDomain, title: { en: string; es: string; ru: string }, desc: { en: string; es: string; ru: string }, severity: 'CRITICAL' | 'MAJOR', evidence: PciDssControl['evidence'], vendor: boolean, rec: Partial<PciDssControl['recommendations']>): PciDssControl => ({
  standard: 'PCI_DSS_2025', controlCode: `PCI_${code}`, domain, title, description: desc, severity, evidence,
  vendorImpact: { applies: vendor }, governanceLinks: { policies: [], processes: [], roles: [] },
  crossMapping: { ISO27001: [] }, recommendations: rec,
});

const PCI_NET: PciDssControl[] = [
  def('NET_01', 'Network', { en: 'Firewall configuration', es: 'Configuración firewall', ru: 'Конфигурация файрвола' },
    { en: 'Firewalls on all network boundaries.', es: 'Firewalls en límites de red.', ru: 'Файрволы на границах сети.' },
    'CRITICAL', [ev('config', ['Firewall rules'], ['Reglas'], ['Правила'])], false,
    { CRITICAL: { en: 'Implement firewall per PCI DSS.', es: 'Implementar firewall según PCI DSS.', ru: 'Внедрить файрвол по PCI DSS.' } }),
  def('NET_02', 'Network', { en: 'Network segmentation', es: 'Segmentación de red', ru: 'Сегментация сети' },
    { en: 'Payment environment segmented.', es: 'Entorno de pagos segmentado.', ru: 'Среда платежей сегментирована.' },
    'CRITICAL', [ev('record', ['Network diagram'], ['Diagrama'], ['Схема'])], false,
    { CRITICAL: { en: 'Implement network segmentation.', es: 'Implementar segmentación.', ru: 'Внедрить сегментацию сети.' } }),
];
const PCI_DATA: PciDssControl[] = [
  def('DATA_01', 'Data Protection', { en: 'Encryption at rest', es: 'Cifrado en reposo', ru: 'Шифрование при хранении' },
    { en: 'Cardholder data encrypted when stored.', es: 'Datos cifrados en reposo.', ru: 'Данные шифруются при хранении.' },
    'CRITICAL', [ev('config', ['Encryption config'], ['Config'], ['Конфиг'])], false,
    { CRITICAL: { en: 'Implement encryption at rest.', es: 'Implementar cifrado en reposo.', ru: 'Внедрить шифрование при хранении.' } }),
  def('DATA_02', 'Data Protection', { en: 'TLS in transit', es: 'TLS en tránsito', ru: 'TLS при передаче' },
    { en: 'TLS 1.2+ for data transmission.', es: 'TLS 1.2+ para transmisión.', ru: 'TLS 1.2+ при передаче.' },
    'CRITICAL', [ev('config', ['TLS config'], ['Config'], ['Конфиг'])], false,
    { CRITICAL: { en: 'Use TLS 1.2+ for transmission.', es: 'Usar TLS 1.2+ para transmisión.', ru: 'Использовать TLS 1.2+ при передаче.' } }),
];
const PCI_ACCESS: PciDssControl[] = [
  def('ACC_01', 'Access', { en: 'Unique user IDs', es: 'IDs únicos por usuario', ru: 'Уникальные учётные записи' },
    { en: 'Unique accounts for each user.', es: 'Cuentas únicas por usuario.', ru: 'Уникальные учётки для каждого пользователя.' },
    'CRITICAL', [ev('procedure', ['Access policy'], ['Política'], ['Политика'])], false,
    { CRITICAL: { en: 'Implement unique user IDs.', es: 'Implementar IDs únicos.', ru: 'Внедрить уникальные учётки.' } }),
  def('ACC_02', 'Access', { en: 'MFA for admins', es: 'MFA para administradores', ru: 'MFA для администраторов' },
    { en: 'MFA for administrative access.', es: 'MFA para acceso administrativo.', ru: 'MFA для административного доступа.' },
    'CRITICAL', [ev('config', ['MFA config'], ['Config'], ['Конфиг'])], false,
    { CRITICAL: { en: 'Implement MFA for admins.', es: 'Implementar MFA para admins.', ru: 'Внедрить MFA для администраторов.' } }),
];

export const PCI_DSS_2025_CONTROLS_CATALOG: PciDssControl[] = [...PCI_NET, ...PCI_DATA, ...PCI_ACCESS];
const DOMAIN_ORDER: PciDssDomain[] = ['Network', 'Data Protection', 'Vulnerability', 'Access', 'Monitoring', 'Policy'];
export function getPciDssControlsByDomain(domain: PciDssDomain): PciDssControl[] {
  return PCI_DSS_2025_CONTROLS_CATALOG.filter((c) => c.domain === domain);
}
export function getPciDssControlDomains(): PciDssDomain[] {
  return DOMAIN_ORDER.filter((d) => PCI_DSS_2025_CONTROLS_CATALOG.some((c) => c.domain === d));
}
